import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Power, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const CATS = [
  { key: "make", label: "Old Meter Make" },
  { key: "status", label: "Old Meter Status" },
  { key: "display_status", label: "Display Status" },
];

export default function OldMeterOptionMaster() {
  const { has } = useAuth();
  const canManage = has("MASTERS.MANAGE");
  const [cat, setCat] = useState("make");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/old-meter-options", { params: { category: cat, include_inactive: true } });
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.value?.trim()) { toast.error("Value is required"); return; }
    try {
      if (form.id) await api.put(`/old-meter-options/${form.id}`, { value: form.value });
      else await api.post("/old-meter-options", { category: cat, value: form.value });
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/old-meter-options/${r.id}/status`, null, { params: { is_active: !r.is_active } }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Old Meter Options" subtitle="Admin-managed quick-pick lists for Make / Status / Display Status used in Data Entry"
        actions={canManage && <Button data-testid="omopt-add" className="bg-blue-600 hover:bg-blue-700" onClick={() => setForm({ value: "" })}><Plus className="mr-1.5 h-4 w-4" /> New Option</Button>} />
      <div className="mb-4 flex gap-2" data-testid="omopt-tabs">
        {CATS.map((c) => (
          <button key={c.key} data-testid={`omopt-tab-${c.key}`} onClick={() => setCat(c.key)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${cat === c.key ? "bg-[var(--mf-purple-600)] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{c.label}</button>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100/80">
              <th className="px-4 py-2.5 text-xs font-semibold uppercase text-slate-500">Value</th>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase text-slate-500">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody data-testid="omopt-rows">
            {loading ? <tr><td colSpan={3} className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={3} className="py-8 text-center text-sm text-slate-400">No options yet — add one to build the quick-pick list</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`omopt-row-${r.id}`}>
                    <td className="px-4 py-2.5 text-sm text-slate-800">{r.value}</td>
                    <td className="px-4 py-2.5"><Badge variant={r.is_active ? "default" : "outline"} className={r.is_active ? "bg-emerald-600" : "text-slate-400"}>{r.is_active ? "Active" : "Inactive"}</Badge></td>
                    <td className="px-4 py-2.5 text-right">{canManage && (
                      <div className="flex justify-end gap-1">
                        <Button data-testid={`omopt-edit-${r.id}`} variant="ghost" size="sm" onClick={() => setForm({ id: r.id, value: r.value })}><Pencil className="h-4 w-4" /></Button>
                        <Button data-testid={`omopt-toggle-${r.id}`} variant="ghost" size="sm" onClick={() => toggle(r)}><Power className={`h-4 w-4 ${r.is_active ? "text-emerald-600" : "text-slate-400"}`} /></Button>
                      </div>
                    )}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">{form?.id ? "Edit Option" : "New Option"} · {CATS.find((c) => c.key === cat)?.label}</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Value *</Label><Input data-testid="omopt-value-input" value={form?.value || ""} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" /></div>
        <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button data-testid="omopt-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
